import { useState, useRef } from "react";
import { Spin } from "antd";
import { FieldRenderProps } from "react-final-form";
import cn from "classnames";
import styles from "./FloatingLabelInput.module.scss";
import { CustomTooltip } from "../title-field/CustomTooltip";
import { LoadingOutlined } from "@ant-design/icons";
import mergeRefs from "merge-refs";
import { withMask } from "use-mask-input";
import { useIsMobile } from "~/hooks";
import { NamesOfFieldEnum } from "~/typings/types";

const antIcon = <LoadingOutlined className={styles.spin} spin />;

interface FloatingLabelInputProps extends Partial<FieldRenderProps<string, HTMLElement>> {
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  titleField?: string;
  disabled?: boolean;
  type?: string;
  maxLength?: number;
  required?: boolean;
  isColon?: boolean;
  tooltip?: string;
  percent?: string;
  isLoading?: boolean;
  allowMultiline?: boolean;
  showCount?: boolean;
  classNameInput?: string;
  mask?: string;
  onFocusCallback?: (e: any) => void;
  onBlurCallback?: (e: any) => void;
}

export const FloatingLabelInput: React.FC<FloatingLabelInputProps> = ({
  input,
  meta,
  value = "",
  onChange,
  titleField,
  disabled,
  type = "text",
  maxLength,
  required = false,
  isColon = false,
  tooltip,
  isLoading,
  allowMultiline = false,
  showCount = false,
  classNameInput,
  mask,
  onFocusCallback,
  onBlurCallback,
  ...props
}) => {
  const isMobile = useIsMobile();
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<any>(null);
  const hasError = (meta?.error || meta?.submitError) && meta?.submitFailed;

  const mergedInputRefs = isFocused && mask ? mergeRefs(inputRef, withMask(mask)) : inputRef;

  const handleLabelClick = () => {
    inputRef.current?.focus();
  };

  const handleLabelBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    // С маской работает не валидно при потери фокуса
    if (!mask) {
      setIsFocused(false);
    }
    onBlurCallback?.(e);
  };

  const inputAddons = (
    <div className={cn(styles.inputAddons, { [styles.inputAddonsTextArea]: allowMultiline })}>
      {isLoading && (
        <Spin
          indicator={antIcon}
          className={styles.containerSpin}
          style={{ right: tooltip ? "30px" : "0px" }}
        />
      )}
      {tooltip && <CustomTooltip tooltip={tooltip} placement="topRight" />}
    </div>
  );

  return (
    <div
      className={cn(styles.inputWrapper, {
        [styles.focused]: isFocused || value,
        [styles.error]: hasError
      })}
    >
      <label
        onClick={handleLabelClick}
        className={cn({
          [styles.placeholderInput]: !allowMultiline,
          [styles.placeholderTextArea]: allowMultiline,
          [styles.text]: required
        })}
      >
        {titleField}
        {isColon && ":"}
      </label>
      {allowMultiline && <div className={styles.background} />}
      <div
        className={cn(styles.inputContainer, {
          [styles.inputContainerTextArea]: allowMultiline
        })}
      >
        {allowMultiline ? (
          <textarea
            {...props}
            ref={inputRef}
            value={input?.value ?? value}
            onChange={input?.onChange ?? onChange}
            onFocus={(e: React.FocusEvent<HTMLTextAreaElement>) => {
              setIsFocused(true);
              onFocusCallback?.(e);
            }}
            onBlur={handleLabelBlur}
            disabled={disabled}
            maxLength={maxLength}
            className={cn(styles.customTextArea, props.className)}
            autoComplete={props?.autoComplete}
          />
        ) : (
          <input
            {...props}
            ref={mergedInputRefs}
            value={input?.value ?? value}
            onChange={input?.onChange ?? onChange}
            onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
              setIsFocused(true);
              onFocusCallback?.(e);
            }}
            onBlur={handleLabelBlur}
            type={type}
            disabled={disabled}
            maxLength={maxLength}
            className={cn(styles.customInput, classNameInput, { [styles.focusedInput]: isFocused })}
            style={{
              paddingRight: isLoading ? "70px" : "40px",
              fontSize:
                props.name === NamesOfFieldEnum.SettlementAccount ||
                props.name === NamesOfFieldEnum.CorrespondentAccount
                  ? "16px"
                  : undefined
            }}
            autoComplete={props?.autoComplete}
          />
        )}

        {inputAddons}

        {maxLength && showCount && value.length > 0 && (
          <div className={cn(styles.charCount, { [styles.charCountMobile]: isMobile })}>
            {`${value.length}/${maxLength}`}
          </div>
        )}
      </div>
    </div>
  );
};
